import React, { useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { FilePdf, UploadSimple, DownloadSimple, X } from "@phosphor-icons/react";
import { toast } from "sonner";

const MAX_BYTES = 5 * 1024 * 1024;

export const validatePdfFiles = async (files) => {
  for (const file of files) {
    const head = new Uint8Array(await file.slice(0, 4).arrayBuffer());
    if (!file.name.toLowerCase().endsWith(".pdf") || String.fromCharCode(...head) !== "%PDF") {
      return `"${file.name}" is not a PDF — only PDF files are allowed`;
    }
    if (file.size > MAX_BYTES) return `"${file.name}" is larger than 5 MB`;
  }
  return "";
};

export const uploadDscPdfs = (dscId, files) => {
  const fd = new FormData();
  files.forEach((f) => fd.append("files", f));
  return api.post(`/dsc/${dscId}/documents`, fd);
};

const DocChip = ({ dscId, doc, canManage, onChanged }) => {
  const [busy, setBusy] = useState(false);

  const download = async () => {
    setBusy(true);
    try {
      const { data } = await api.get(`/dsc/${dscId}/documents/${doc.id}`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = doc.name || "document.pdf";
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (err) { toast.error("Download failed"); }
    finally { setBusy(false); }
  };

  const remove = async () => {
    if (!window.confirm(`Delete "${doc.name}"?`)) return;
    setBusy(true);
    try {
      await api.delete(`/dsc/${dscId}/documents/${doc.id}`);
      toast.success("PDF deleted");
      onChanged();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  return (
    <div className="flex items-center gap-1" data-testid={`dsc-doc-item-${doc.id}`}>
      <span className="inline-flex items-center gap-1 text-[11px] max-w-[110px] truncate" title={doc.name}>
        <FilePdf size={14} className="text-red-600 shrink-0" /> {doc.name}
      </span>
      {canManage && (
        <>
          <Button size="sm" variant="ghost" className="h-6 w-6 p-0" disabled={busy} title="Download PDF"
            onClick={download} data-testid={`dsc-doc-download-${doc.id}`}>
            <DownloadSimple size={13} />
          </Button>
          <Button size="sm" variant="ghost" className="h-6 w-6 p-0 text-destructive" disabled={busy} title="Delete PDF"
            onClick={remove} data-testid={`dsc-doc-delete-${doc.id}`}>
            <X size={12} />
          </Button>
        </>
      )}
    </div>
  );
};

export const DscDocumentCell = ({ row, onChanged }) => {
  const { user } = useAuth();
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const canManage = ["admin", "manager", "regional_manager"].includes(user?.role);
  const docs = row.documents || [];

  const upload = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    const err = await validatePdfFiles(files);
    if (err) { toast.error(err); return; }
    setBusy(true);
    try {
      await uploadDscPdfs(row.id, files);
      toast.success(`${files.length} PDF(s) uploaded`);
      onChanged();
    } catch (er) { toast.error(formatApiError(er)); }
    finally { setBusy(false); }
  };

  return (
    <div className="flex flex-col gap-0.5" data-testid={`dsc-doc-${row.id}`}>
      {docs.map((d) => <DocChip key={d.id} dscId={row.id} doc={d} canManage={canManage} onChanged={onChanged} />)}
      <div className="flex items-center gap-1">
        {!docs.length && (
          <span className="pill text-[10px] text-destructive border-destructive" data-testid={`dsc-doc-missing-${row.id}`}>PDF missing</span>
        )}
        <input ref={inputRef} type="file" multiple accept="application/pdf,.pdf" className="hidden"
          onChange={upload} data-testid={`dsc-doc-input-${row.id}`} />
        <Button size="sm" variant="outline" className="h-6 px-2 text-[10.5px]" disabled={busy}
          title="Add PDF(s)" onClick={() => inputRef.current?.click()} data-testid={`dsc-doc-upload-${row.id}`}>
          <UploadSimple size={12} className="mr-1" /> {docs.length ? "Add PDF" : "Upload"}
        </Button>
      </div>
    </div>
  );
};
